import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MenuIcon, XIcon, GlobeIcon, ChevronDownIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
import type { Lang } from '../context/LanguageContext';
const languages: Lang[] = ['UA', 'RU', 'EN'];
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const {
    lang,
    setLang
  } = useLanguage();
  const t = useTranslations(lang);
  const navItems = [{
    label: t.nav.about,
    href: '/#about'
  }, {
    label: t.nav.services,
    href: '/#services'
  }, {
    label: t.nav.employers,
    href: '/#process'
  }, {
    label: t.nav.contacts,
    href: '/#contact'
  }];
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-navy-200' : 'bg-transparent'}`} data-id="element-246">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-247">
        <div className="flex items-center justify-between h-16 lg:h-20" data-id="element-248">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-id="element-249">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-sm" data-id="element-250">
              <span className="text-white font-extrabold text-xs tracking-wider" data-id="element-251">
                ESCA
              </span>
            </div>
            <div className="flex flex-col" data-id="element-252">
              <span className="font-extrabold text-lg tracking-tight text-navy-900 leading-none" data-id="element-253">
                ESCA
              </span>
              <span className="text-[9px] font-medium text-navy-400 tracking-widest uppercase leading-none mt-0.5" data-id="element-254">
                Executive Search
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation" data-id="element-255">
            {navItems.map(item => {
            const isRouterLink = item.href === '/blog';
            if (isRouterLink) {
              return <Link key={item.href} to={item.href} className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isScrolled ? 'text-navy-600 hover:text-primary-600 hover:bg-primary-50' : 'text-navy-700 hover:text-primary-600 hover:bg-white/60'}`} data-id="element-256">
                    {item.label}
                  </Link>;
            }
            return <a key={item.href} href={item.href} className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isScrolled ? 'text-navy-600 hover:text-primary-600 hover:bg-primary-50' : 'text-navy-700 hover:text-primary-600 hover:bg-white/60'}`} data-id="element-257">
                  {item.label}
                </a>;
          })}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3" data-id="element-258">
            {/* Language Switcher */}
            <div className="relative hidden sm:block" data-id="element-259">
              <button onClick={() => setLangOpen(!langOpen)} className={`flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium rounded-lg transition-colors ${isScrolled ? 'text-navy-500 hover:text-navy-700 hover:bg-navy-100' : 'text-navy-600 hover:text-navy-800 hover:bg-white/60'}`} aria-label="Select language" data-id="element-260">
                <GlobeIcon className="w-4 h-4" data-id="element-261" />
                {lang}
                <ChevronDownIcon className={`w-3 h-3 transition-transform ${langOpen ? 'rotate-180' : ''}`} data-id="element-262" />
              </button>
              <AnimatePresence data-id="element-263">
                {langOpen && <motion.div initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -4
              }} transition={{
                duration: 0.15
              }} className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border border-navy-200 overflow-hidden min-w-[80px]" data-id="element-264">
                    {languages.map(l => <button key={l} onClick={() => {
                  setLang(l);
                  setLangOpen(false);
                }} className={`block w-full text-left px-4 py-2 text-sm transition-colors ${lang === l ? 'bg-primary-50 text-primary-700 font-medium' : 'text-navy-600 hover:bg-navy-50'}`} data-id="element-265">
                        {l}
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* CTA Button */}
            <a href="/#contact" className="hidden sm:inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors shadow-sm shadow-primary-600/20" data-id="element-266">
              {t.header.cta}
            </a>

            {/* Mobile toggle */}
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 rounded-lg text-navy-600 hover:bg-navy-100 transition-colors" aria-label={mobileOpen ? 'Close menu' : 'Open menu'} data-id="element-267">
              {mobileOpen ? <XIcon className="w-5 h-5" data-id="element-268" /> : <MenuIcon className="w-5 h-5" data-id="element-269" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-270">
        {mobileOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.25
      }} className="lg:hidden bg-white border-t border-navy-200 overflow-hidden" data-id="element-271">
            <nav className="px-4 py-4 space-y-1" aria-label="Mobile navigation" data-id="element-272">
              {navItems.map(item => {
            const isRouterLink = item.href === '/blog';
            if (isRouterLink) {
              return <Link key={item.href} to={item.href} onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-sm font-medium text-navy-700 rounded-lg hover:bg-primary-50 hover:text-primary-600 transition-colors" data-id="element-273">
                      {item.label}
                    </Link>;
            }
            return <a key={item.href} href={item.href} onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-sm font-medium text-navy-700 rounded-lg hover:bg-primary-50 hover:text-primary-600 transition-colors" data-id="element-274">
                    {item.label}
                  </a>;
          })}
              <div className="pt-3 border-t border-navy-100 mt-3 flex items-center gap-2" data-id="element-275">
                {languages.map(l => <button key={l} onClick={() => setLang(l)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${lang === l ? 'bg-primary-600 text-white font-medium' : 'text-navy-500 hover:bg-navy-100'}`} data-id="element-276">
                    {l}
                  </button>)}
              </div>
              <a href="/#contact" onClick={() => setMobileOpen(false)} className="block mt-3 text-center px-5 py-3 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors" data-id="element-277">
                {t.header.cta}
              </a>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}